import {
  type DocumentData, type Firestore, collection, doc, getDoc,
  serverTimestamp, setDoc, updateDoc,
} from 'firebase/firestore';
import { VoucherSchema, VoucherCreateInputSchema, VoucherUpdateInputSchema, type Voucher, type VoucherCreateInput, type VoucherUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

export type { VoucherCreateInput };
export const VOUCHERS_COLLECTION = 'vouchers';

export interface VoucherRepository {
  create(input: VoucherCreateInput): Promise<Voucher>;
  update(id: string, patch: VoucherUpdateInput): Promise<Voucher>;
  list(options?: ListOptions): Promise<Voucher[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<Voucher | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Voucher>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Voucher>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Voucher>;
}

const voucherConverter = createConverter<Voucher>((raw: DocumentData) => VoucherSchema.parse(raw));

export function createVoucherRepository(db: Firestore, getActorId: () => string, orgId: string): VoucherRepository {
  const col = collection(db, VOUCHERS_COLLECTION).withConverter(voucherConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Voucher>(db, col, VOUCHERS_COLLECTION, getActorId);
  return {
    async create(input: VoucherCreateInput): Promise<Voucher> {
      const fields = VoucherCreateInputSchema.parse(input); // valider AVANT d'ecrire
      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();
      const optional: DocumentData = {};
      if (fields.initialValue !== undefined) optional['initialValue'] = fields.initialValue;
      if (fields.remainingValue !== undefined) optional['remainingValue'] = fields.remainingValue;
      if (fields.expiresAt !== undefined) optional['expiresAt'] = fields.expiresAt;
      if (fields.issuedToContactId !== undefined) optional['issuedToContactId'] = fields.issuedToContactId;
      if (fields.redeemedRefs !== undefined) optional['redeemedRefs'] = fields.redeemedRefs;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
      if (fields.couleur != null) optional['couleur'] = fields.couleur;
      if (fields.photo != null) optional['photo'] = fields.photo;
      const payload: DocumentData = {
        orgId: fields.orgId, type: fields.type, code: fields.code, ...optional,
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        createdBy: actor, updatedBy: actor, archivedAt: null, deletedAt: null, schemaVersion: CURRENT_SCHEMA_VERSION,
      };
      await setDoc(ref, payload as unknown as Voucher);
      return VoucherSchema.parse({ id: ref.id, ...payload, createdAt: now, updatedAt: now });
    },
    async update(id: string, patch: VoucherUpdateInput): Promise<Voucher> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = VoucherUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, VOUCHERS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`voucher ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },
    async list(options?: ListOptions): Promise<Voucher[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      // Sommer sans charger, meme cadrage que `count` (cf. runSum,
      // list-query.ts). Rend 0 quand aucun document ne passe le filtre.
      return runSum(col, orgId, field, options);
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      // `null` quand aucun document ne passe le filtre : c'est ce que rend
      // Firestore, et un zero mentirait (cf. runAverage, list-query.ts).
      return runAverage(col, orgId, field, options);
    },
    async get(id: string): Promise<Voucher | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
